import type { AxiosCacheInstance } from 'axios-cache-interceptor';

import type {
  GoStorageObject,
  IDashboardService,
  SelectRequestConfig,
  VideoBroadcast,
  Wellbore,
} from './IDashboardService';

export class DashboardService implements IDashboardService {
  baseURL: string = '/service/v1';

  constructor(private axios: AxiosCacheInstance) {}

  getVideoBroadcast(wellId: number, abortController?: AbortController) {
    return this.axios
      .get<VideoBroadcast[]>(`${this.baseURL}/wells/${wellId}/videoBroadcasts`, {
        signal: abortController?.signal,
        cache: {
          ttl: 1000 * 10,
        },
      })
      .then(({ data }) => data);
  }

  getWellbores(wellId: number, abortController?: AbortController) {
    return this.axios
      .get<Wellbore[]>(`${this.baseURL}/wells/${wellId}/wellbores`, {
        signal: abortController?.signal,
        cache: {
          ttl: 1000 * 5,
        },
      })
      .then(({ data }) => data);
  }

  selectData<T>(config: SelectRequestConfig, abortController?: AbortController): Promise<GoStorageObject<T>[]> {
    return this.axios
      .post<Record<string, GoStorageObject<T>>[]>(`${this.baseURL}/object/select?flatResponse=false`, config, {
        signal: abortController?.signal,
      })
      .then(({ data }) =>
        data.map((item) => {
          const key = config.joinedAlias;
          return item[key];
        }),
      );
  }

  createGoStorageObject<T>(objectType: string, data: T): Promise<GoStorageObject<T>> {
    return this.axios
      .post<GoStorageObject<T>>(`${this.baseURL}/object/new/${objectType}`, data)
      .then(({ data }) => data);
  }

  updateGoStorageObject<T>(objectType: string, id: number, data: T): Promise<GoStorageObject<T>> {
    return this.axios
      .post<GoStorageObject<T>>(`${this.baseURL}/object/${objectType}/${id}`, data)
      .then(({ data }) => data);
  }

  deleteGoStorageObject(objectType: string, id: number): Promise<void> {
    return this.axios.delete(`${this.baseURL}/object/${objectType}/${id}`);
  }
}
